import { useState, useRef, useEffect, useCallback } from 'react';

interface QueryState {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

interface UseMultiInfiniteScrollOptions {
  queries: QueryState[];
}

interface UseMultiInfiniteScrollReturn {
  sentinelRef: (node: HTMLDivElement | null) => void;
  hasAnyNextPage: boolean;
  isAnyFetchingNextPage: boolean;
}

export const useMultiInfiniteScroll = ({
  queries,
}: UseMultiInfiniteScrollOptions): UseMultiInfiniteScrollReturn => {
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  const hasAnyNextPage = queries.some((q) => q.hasNextPage);
  const isAnyFetchingNextPage = queries.some((q) => q.isFetchingNextPage);

  // Store latest queries in a ref so the observer callback always sees
  // current values without needing to tear down and re-attach.
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  const fetchingRef = useRef(isAnyFetchingNextPage);
  fetchingRef.current = isAnyFetchingNextPage;

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries[0]?.isIntersecting && !fetchingRef.current) {
      queriesRef.current.forEach((q) => {
        if (q.hasNextPage) q.fetchNextPage();
      });
    }
  }, []);

  useEffect(() => {
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersect, { rootMargin: '200px' });
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [sentinel, handleIntersect]);

  return { sentinelRef: setSentinel, hasAnyNextPage, isAnyFetchingNextPage };
};
